import { useEffect } from 'react'
import { useNextBestAction } from '@/hooks/useOpportunities'
import { Button } from '@/components/ui/Button'
import { Sparkles, Loader2, X, Mail, MessageCircle, Phone, Calendar, FileText, AlertTriangle } from 'lucide-react'

interface Props {
  oppId: string
  onClose: () => void
}

const CHANNEL_ICON: Record<string, typeof Mail> = {
  email: Mail,
  whatsapp: MessageCircle,
  phone: Phone,
  meeting: Calendar,
  note: FileText,
}

const CHANNEL_LABEL: Record<string, string> = {
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  phone: 'Ligação',
  meeting: 'Reunião',
  note: 'Anotação interna',
}

const URGENCY_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  high: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Alta urgência' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: 'Urgência média' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', label: 'Baixa urgência' },
}

export function NextBestActionModal({ oppId, onClose }: Props) {
  const nba = useNextBestAction()

  useEffect(() => {
    nba.mutate(oppId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppId])

  const data = nba.data
  const ChannelIcon = data ? (CHANNEL_ICON[data.channel] || FileText) : FileText
  const urgency = data ? (URGENCY_STYLE[data.urgency] || URGENCY_STYLE.medium) : URGENCY_STYLE.medium

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background border rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold leading-tight">Próxima melhor ação</h3>
              <p className="text-xs text-muted-foreground">Sugestão da IA com base no estado atual da oportunidade</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {nba.isPending && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Analisando contexto da oportunidade…</p>
            </div>
          )}

          {nba.isError && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Não foi possível gerar sugestão</div>
                <div className="text-xs mt-1 opacity-80">
                  {(nba.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Erro inesperado. Tente novamente em instantes.'}
                </div>
              </div>
            </div>
          )}

          {data && (
            <>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium w-fit ${urgency.bg} ${urgency.text}`}>
                <AlertTriangle className="h-3 w-3" />
                {urgency.label}
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Ação sugerida</div>
                <div className="text-base font-medium leading-snug">{data.action}</div>
              </div>

              <div className="grid grid-cols-2 gap-3 py-2 border-y">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Canal</div>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <ChannelIcon className="h-4 w-4" />
                    {CHANNEL_LABEL[data.channel] || data.channel}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Prazo</div>
                  <div className="text-sm font-medium">
                    {data.deadline_days <= 0 ? 'Hoje' : data.deadline_days === 1 ? 'Amanhã' : `Em ${data.deadline_days} dias`}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Por quê</div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{data.reasoning}</p>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          {data && (
            <Button variant="outline" size="sm" onClick={() => nba.mutate(oppId)} disabled={nba.isPending}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Gerar outra sugestão
            </Button>
          )}
          <Button size="sm" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  )
}
